import * as React from 'react';
import styled from 'styled-components';

import { Form } from '@longline/aqua-ui/containers/Form';
import { Slider } from '@longline/aqua-ui/inputs/Slider';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';

import { IArea, TSimulationKind, isDeposition } from '../../../types/IArea';
import { useGlobalState } from '../../../contexts/state/GlobalStateContext';
import { Grid } from '../../../ui/Grid';
import { ModelControl } from '../../../ui/inputs/ModelControl';
import { SolverControl } from '../../../ui/inputs/SolverControl';
import { Can } from '../../../ui/Can';
import { LinkButton } from '../../../ui/LinkButton';

interface IProps {
  value?: IArea;
}

const SimulateTab = (props: IProps) => {

  const globalState = useGlobalState();
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  // Optional because the parent <Tabs> may render before an area is selected.
  if (!props.value) return null;
  const area = props.value;
  // Faeces / feed pellet diameters only apply to the deposition models.
  const showDepositionFields = isDeposition(area.simulationKind);

  const handleUpdate = (field: string, value: any) => {
    (area as any)[field] = value;
    globalState.updateArea(area);
  }

  return (
    <>
      <Grid>
        <Grid.Item weight={6}>
          {/* min 1, max 20, step: 1 */}
          <Form.Field
            label={'Structure depth'}
            name="structureDepth"
            hint={<>Structure depth in meters.</>}
            wrapper='box'
            value={(area.structureDepth || 6)}
            onChange={(v: any) => handleUpdate("structureDepth", v)}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={1} max={20} ticks={20} color="white"/>}
          />
        </Grid.Item>
        <Grid.Item weight={6}>
          <Form.Field
            label="Model"
            name="simulationKind"
            hint={<>Simulation model.</>}
            wrapper='box'
            value={area.simulationKind}
            onChange={(v: TSimulationKind) => handleUpdate("simulationKind", v)}
            weight={1}
            rules={{ required: {} }}
            control={<ModelControl/>}
          />
        </Grid.Item>
        {showDepositionFields && <Can permission='simulated_advanced'>
          <Grid.Item weight={3}>
            {/* min 0.01, max 10, step: 0.05 */}
            <Form.Field
              label={'Faeces \u00d8'}
              name="faecesDiameter"
              hint={<>mm</>}
              wrapper='box'
              value={(area.faecesDiameter || 0.01) * 100}
              onChange={(v: any) => handleUpdate("faecesDiameter", v / 100)}
              weight={1}
              rules={{ required: {} }}
              control={<Slider padded min={1} max={1000} formatter={(v) => (v / 100).toFixed(2)} color="white"/>}
            />
          </Grid.Item>
        </Can>}
        {showDepositionFields && <Can permission='simulated_advanced'>
          <Grid.Item weight={3}>
            {/* min 0.01, max 20, step: 0.05 */}
            <Form.Field
              label={'Feed pellet \u00d8'}
              name="feedPelletDiameter"
              hint={<>mm</>}
              wrapper='box'
              value={(area.feedPelletDiameter || 0.01) * 100}
              onChange={(v: any) => handleUpdate("feedPelletDiameter", v / 100)}
              weight={1}
              rules={{ required: {} }}
              control={<Slider padded min={1} max={2000} formatter={(v) => (v / 100).toFixed(2)} color="white"/>}
            />
          </Grid.Item>
        </Can>}
        <Grid.Item weight={3}>
          {/* min 0, max 0.2, step: 0.01, default: 0.05 */}
          <Form.Field
            label='X dispersion'
            name="xDispersion"
            hint={<>m² s⁻¹</>}
            wrapper='box'
            value={(area.xDispersion || 0.00) * 10000}
            onChange={(v: any) => handleUpdate("xDispersion", v / 10000)}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={0} max={2000} formatter={(v) => (v / 10000).toFixed(2)} color="white"/>}
          />
        </Grid.Item>
        <Grid.Item weight={3}>
          {/* min 0, max 0.2, step: 0.01, default: 0.05 */}
          <Form.Field
            label='Y dispersion'
            name="yDispersion"
            hint={<>m² s⁻¹</>}
            wrapper='box'
            value={(area.yDispersion || 0.00) * 10000}
            onChange={(v: any) => handleUpdate("yDispersion", v / 10000)}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={0} max={2000} formatter={(v) => (v / 10000).toFixed(2)} color="white"/>}
          />
        </Grid.Item>        
      </Grid>

      <ButtonContainer>
        <TertiaryButton onClick={() => setShowAdvanced(!showAdvanced)}>
          {showAdvanced ? 'Hide Advanced' : 'Advanced'}
        </TertiaryButton>
      </ButtonContainer>

      {showAdvanced && <Grid>
        <Grid.Item weight={6}>
          <Form.Field
            label="Solver"
            name="solver"
            hint={<>Numerical solver for the simulation.</>}
            wrapper='box'
            value={area.solver || 0}
            onChange={(v: number) => {
              handleUpdate("timestepReductionFactor", v == 0 ? 1 : 2);
              handleUpdate("solver", v);
            }}
            weight={1}
            rules={{ required: {} }}
            control={<SolverControl/>}
          />
        </Grid.Item>
        <Grid.Item weight={6}>
          <Form.Field
            label="Timestep reduction factor"
            name="timestepReductionFactor"
            hint={<>Reduces the solver's timestep.</>}
            wrapper='box'
            value={area.timestepReductionFactor || 1}
            onChange={(v: any) => handleUpdate("timestepReductionFactor", v)}
            weight={1}
            rules={{ required: {} }}
            control={<Slider padded min={1} max={20} ticks={20} color="white"/>}
          />
        </Grid.Item>
      </Grid>}

      <Can permission="exportOrganix">
        <ExportRow>
          <LinkButton onClick={() => globalState.startOrganixExport(area)}>
            Export ORGANIX data
          </LinkButton>
        </ExportRow>
      </Can>
    </>
  )
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0;
`

const ExportRow = styled.div`
  display: flex;
  justify-content: end;
  padding: 8px 0;
`

export { SimulateTab }
